// Feature: docs/features/org-default-branding.md
// Feature: docs/features/org-default-branding.md
import React, { useState, useEffect, useContext } from 'react';
import {
  Box, Button, VStack, HStack, Heading, Text, Table, Thead, Tbody, Tr, Th, Td,
  useToast, Input, FormControl, FormLabel, IconButton, Badge, Image, Card, CardBody,
  Container, Select
} from '@chakra-ui/react';
import { DeleteIcon, StarIcon } from '@chakra-ui/icons';
import { Divider } from '@chakra-ui/react';
import HeaderMenus from './HeaderMenus';
import { callApi } from '../callApi';
import { AppContext } from './AppContext';

const BrandingManager = () => {
  const { accessToken } = useContext(AppContext);
  const [logos, setLogos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [newLogo, setNewLogo] = useState(null);
  const [logoName, setLogoName] = useState('');
  const toast = useToast();

  const fetchLogos = async () => {
    setLoading(true);
    try {
      const response = await callApi('/api/branding/logos', accessToken);
      if (response.ok) {
        const data = await response.json();
        setLogos(data);
      }
    } catch (error) {
      toast({ title: 'Error fetching logos', status: 'error', duration: 3000 });
    } finally {
      setLoading(false);
    }
  };


  useEffect(() => {
    if (accessToken) {
      fetchLogos();
    }
  }, [accessToken]);


  const handleUpload = async () => {
    if (!newLogo) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('logo', newLogo);
    formData.append('name', logoName || newLogo.name);
    formData.append('is_default', logos.length === 0);

    try {
      const response = await callApi('/api/branding/logos', accessToken, {
        method: 'POST',
        body: formData
      });
      if (response.ok) {
        toast({ title: 'Logo uploaded', status: 'success' });
        setLogoName('');
        setNewLogo(null);
        fetchLogos();
      }
    } catch (error) {
      toast({ title: 'Upload failed', status: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const deleteLogo = async (id) => {
    try {
      const response = await callApi(`/api/branding/logos/${id}`, accessToken, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast({ title: 'Logo deleted', status: 'info' });
        fetchLogos();
      }
    } catch (error) {
      toast({ title: 'Delete failed', status: 'error' });
    }
  };

  const apiHost = window.env?.REACT_APP_REVEDY_API_HOST || '';

  return (
    <Box>
      <HeaderMenus />
      <Container maxW="container.xl" py={8}>
        <VStack align="stretch" spacing={6}>
        <Heading size="lg">Organization Branding</Heading>
        <Text color="gray.600">Manage logos for your Medical Necessity reports.</Text>

        <Card variant="outline">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Heading size="sm">Upload New Logo</Heading>
              <HStack>
                <FormControl>
                  <FormLabel>Logo Name</FormLabel>
                  <Input value={logoName} onChange={(e) => setLogoName(e.target.value)} placeholder="e.g. Main Clinic Logo" />
                </FormControl>
                <FormControl>
                  <FormLabel>File</FormLabel>
                  <Input type="file" accept="image/*" onChange={(e) => setNewLogo(e.target.files[0])} />
                </FormControl>
                <Button mt={8} colorScheme="blue" isLoading={uploading} onClick={handleUpload} isDisabled={!newLogo}>
                  Upload
                </Button>
              </HStack>
            </VStack>
          </CardBody>
        </Card>

        <Table variant="simple">
          <Thead>
            <Tr>
              <Th>Preview</Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {logos.map((logo) => (
              <Tr key={logo.id}>
                <Td>
                  <Image 
                    src={`${process.env.REACT_APP_API_URL || ''}/api/branding/logos/${logo.id}/image`} 
                    maxH="40px" 
                    fallbackSrc="https://via.placeholder.com/40"
                  />
                </Td>
                <Td>{logo.name}</Td>
                <Td>
                  {logo.is_default && <Badge colorScheme="yellow"><StarIcon mr={1} /> Default</Badge>}
                </Td>
                <Td>
                  <IconButton icon={<DeleteIcon />} colorScheme="red" variant="ghost" onClick={() => deleteLogo(logo.id)} />
                </Td>
              </Tr>
            ))}
          </Tbody>
        </Table>
      </VStack>
      </Container>
    </Box>
  );
};

export default BrandingManager;